import React, { useState, useCallback, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialMode?: 'login' | 'register';
}

const LoginModal: React.FC<LoginModalProps> = ({ isOpen, onClose, initialMode = 'login' }) => {
  const { login, register } = useAuth();
  const [showRegister, setShowRegister] = useState(initialMode === 'register');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // All hooks must be called before any early returns
  const resetForm = useCallback(() => {
    setEmail('');
    setPassword('');
    setFullName('');
    setError('');
    setLoading(false);
  }, []);

  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [resetForm, onClose]);

  const switchMode = useCallback(() => {
    setShowRegister(!showRegister);
    resetForm();
  }, [showRegister, resetForm]);

  // Update showRegister when initialMode changes
  useEffect(() => {
    if (isOpen) {
      setShowRegister(initialMode === 'register');
    }
  }, [isOpen, initialMode]);

  // Get current video based on mode
  const currentVideo = showRegister
    ? '/login/sign_up/keiko_sign_up.mp4'
    : '/login/sign_in/keiko_sign_in.mp4';

  // Early return after all hooks
  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (showRegister) {
        await register({ email, password, full_name: fullName });
        setShowRegister(false);
        setError('Registration successful! Please log in.');
      } else {
        await login({ email, password });
        onClose();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };



  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[9999] p-4">
      <div className="bg-dark-900/80 rounded-2xl border border-dark-600/50 w-full max-w-4xl relative overflow-hidden">
        <div className="flex min-h-[600px]">
          {/* Left Side - DIRECT VIDEO ONLY */}
          <div className="hidden md:flex md:w-1/2 relative overflow-hidden">
            {/* DIRECT VIDEO - NO WRAPPERS */}
              {/* DIRECT VIDEO ELEMENT - NO REACT OVERHEAD */}
              <video
                key={currentVideo}
                src={currentVideo}
                autoPlay
                muted
                loop
                playsInline
                preload="auto"
                disablePictureInPicture
                className="w-full h-full object-cover"
                style={{
                  // MAXIMUM PERFORMANCE - NO TRANSITIONS, NO OPACITY CHANGES
                  transform: 'translate3d(0, 0, 0)',
                  backfaceVisibility: 'hidden',
                  willChange: 'auto', // Let browser decide
                  // Remove all performance-killing properties
                  transition: 'none',
                  animation: 'none',
                  // Force immediate display
                  opacity: 1,
                  display: 'block'
                }}
                onLoadedData={() => {
                  console.log('🎬 Direct video loaded - maximum performance mode');
                }}
                onError={(e) => {
                  console.error('🚫 Direct video failed:', e);
                }}
              />
          </div>

          {/* Right Side - Form */}
          <div className="w-full md:w-1/2 p-8 relative bg-dark-900/60">
            {/* Close Button */}
            <button
              onClick={handleClose}
              className="absolute top-4 right-4 text-dark-400 hover:text-white transition-colors z-10"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <div className="h-full flex flex-col justify-center max-w-sm mx-auto">
              {/* Header */}
              <div className="text-center mb-8">
                <h2 className="text-3xl font-display font-bold text-white mb-2">
                  {showRegister ? 'Create Account' : 'Sign in'}
                </h2>
                <p className="text-dark-300">
                  {showRegister ? 'Join our community today' : 'Welcome back! Please sign in to your account'}
                </p>
              </div>

              {/* Tab Switcher */}
              <div className="flex mb-8">
                <div className="glass-effect backdrop-blur-lg bg-dark-800/30 rounded-xl p-1 border border-dark-600/50 w-full shadow-lg">
                  <div className="flex">
                    <button
                      onClick={() => setShowRegister(false)}
                      className={`flex-1 px-4 py-2 rounded-lg font-medium text-sm ${!showRegister
                        ? 'bg-gradient-to-r from-accent-purple to-accent-pink text-white'
                        : 'text-dark-300 hover:text-white'
                        }`}
                    >
                      Login
                    </button>
                    <button
                      onClick={() => setShowRegister(true)}
                      className={`flex-1 px-4 py-2 rounded-lg font-medium text-sm ${showRegister
                        ? 'bg-gradient-to-r from-accent-purple to-accent-pink text-white'
                        : 'text-dark-300 hover:text-white'
                        }`}
                    >
                      Register
                    </button>
                  </div>
                </div>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-5">
                {showRegister && (
                  <div>
                    <label className="block text-sm font-medium text-dark-300 mb-2">
                      Full Name
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        value={fullName}
                        onChange={(e) => setFullName(e.target.value)}
                        className="w-full px-4 py-3 bg-dark-800/30 border border-dark-600/50 rounded-xl text-white placeholder-dark-400 focus:border-accent-purple focus:bg-dark-800/50 focus:outline-none pl-12"
                        placeholder="Enter your full name"
                        required
                      />
                      <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-dark-400">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                      </div>
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-dark-300 mb-2">
                    E-mail
                  </label>
                  <div className="relative">
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full px-4 py-3 bg-dark-800/30 border border-dark-600/50 rounded-xl text-white placeholder-dark-400 focus:border-accent-purple focus:bg-dark-800/50 focus:outline-none pl-12"
                      placeholder="Enter your email"
                      required
                    />
                    <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-dark-400">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                      </svg>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-dark-300 mb-2">
                    Password
                  </label>
                  <div className="relative">
                    <input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full px-4 py-3 bg-dark-800/30 border border-dark-600/50 rounded-xl text-white placeholder-dark-400 focus:border-accent-purple focus:bg-dark-800/50 focus:outline-none pl-12"
                      placeholder="Enter your password"
                      required
                    />
                    <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-dark-400">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                    </div>
                  </div>
                </div>

                {!showRegister && (
                  <div className="flex justify-end">
                    <button
                      type="button"
                      className="text-sm text-accent-purple hover:text-accent-pink transition-colors underline"
                    >
                      Forgot password?
                    </button>
                  </div>
                )}

                {error && (
                  <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-3 text-red-400 text-sm text-center">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3 bg-gradient-to-r from-accent-purple to-accent-pink text-white font-semibold rounded-xl disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Please wait...' : (showRegister ? 'Create Account' : 'Sign In')}
                </button>
              </form>

              {/* Social Login Options */}
              <div className="mt-6">
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-dark-600"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-dark-900 text-dark-400">
                      {showRegister ? 'or sign up with' : 'or sign in with'}
                    </span>
                  </div>
                </div>

                <div className="mt-4 grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    className="w-full inline-flex justify-center py-2 px-4 border border-dark-600/50 rounded-lg bg-dark-800/30 backdrop-blur-sm text-sm font-medium text-white hover:bg-dark-700/50 hover:backdrop-blur-md transition-all duration-300 shadow-lg"
                  >
                    <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                      <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                      <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                      <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                      <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                    </svg>
                    Google
                  </button>
                  <button
                    type="button"
                    className="w-full inline-flex justify-center py-2 px-4 border border-dark-600/50 rounded-lg bg-dark-800/30 backdrop-blur-sm text-sm font-medium text-white hover:bg-dark-700/50 hover:backdrop-blur-md transition-all duration-300 shadow-lg"
                  >
                    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M13.6823 10.6218L20.2391 3H18.6854L12.9921 9.61788L8.44486 3H3.2002L10.0765 13.0074L3.2002 21H4.75404L10.7663 14.0113L15.5685 21H20.8131L13.6819 10.6218H13.6823ZM11.5541 13.0956L10.8574 12.0991L5.31391 4.16971H7.70053L12.1742 10.5689L12.8709 11.5655L18.6861 19.8835H16.2995L11.5541 13.0956Z" />
                    </svg>
                    Twitter
                  </button>
                </div>
              </div>

              {/* Footer */}
              <div className="text-center mt-8">
                <p className="text-dark-400 text-sm">
                  {showRegister ? 'Already have an account?' : "Don't have an account yet?"}{' '}
                  <button
                    onClick={switchMode}
                    className="text-accent-purple hover:text-accent-pink transition-colors font-medium"
                  >
                    {showRegister ? 'Sign in' : 'Sign up'}
                  </button>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginModal;